/**
 * Marquee API Service
 * Handles all marquee-related API calls for both public and admin endpoints
 */

import api from './api';
import type {
  ApiResponse,
} from './api';
import type {
  MarqueePublic,
  MarqueeAdmin,
  CreateMarqueeDTO,
  UpdateMarqueeDTO,
  MarqueeSettings,
  MarqueeOrderItem,
  GetMarqueesQuery,
} from '../types';

/**
 * Get active marquees (public endpoint)
 * Returns only active marquee items sorted by display order
 */
export async function getMarquees(): Promise<ApiResponse<MarqueePublic[]>> {
  const response = await api.get<MarqueePublic[]>('/api/marquees');

  if (response.success && response.data) {
    return {
      success: true,
      data: response.data,
    };
  }

  return {
    success: false,
    error: response.error,
  };
}

/**
 * Get marquee settings (public endpoint)
 * Returns full marquee settings including enabled status and default values
 * Note: Uses /api/marquees endpoint which returns { success: true, data: [...], settings: {...} }
 */
export async function getMarqueeSettings(): Promise<ApiResponse<MarqueeSettings>> {
  // Use getFull to get the complete backend response including 'settings' property
  const response = await api.getFull<{ success: boolean; data: MarqueePublic[]; settings: MarqueeSettings }>('/api/marquees');

  if (response.success && response.data && response.data.settings) {
    return {
      success: true,
      data: response.data.settings,
    };
  }

  return {
    success: false,
    error: response.error,
  };
}

/**
 * Admin: Get all marquees (including inactive)
 * Requires moderator+ role
 */
export async function getAllMarquees(params?: GetMarqueesQuery): Promise<ApiResponse<MarqueeAdmin[]>> {
  const queryParams: Record<string, string | number | boolean> = {};

  if (params?.isActive !== undefined) {
    queryParams.isActive = String(params.isActive);
  }
  if (params?.limit !== undefined) {
    queryParams.limit = params.limit;
  }
  if (params?.offset !== undefined) {
    queryParams.offset = params.offset;
  }

  return api.get<MarqueeAdmin[]>('/api/admin/marquees', queryParams);
}

/**
 * Admin: Create a new marquee
 * Requires moderator+ role
 */
export async function createMarquee(data: CreateMarqueeDTO): Promise<ApiResponse<MarqueeAdmin>> {
  return api.post<MarqueeAdmin>('/api/admin/marquees', data);
}

/**
 * Admin: Update an existing marquee
 * Requires moderator+ role
 */
export async function updateMarquee(
  id: string,
  data: UpdateMarqueeDTO
): Promise<ApiResponse<MarqueeAdmin>> {
  return api.put<MarqueeAdmin>(`/api/admin/marquees/${id}`, data);
}

/**
 * Admin: Delete a marquee
 * Requires moderator+ role
 */
export async function deleteMarquee(id: string): Promise<ApiResponse<void>> {
  return api.delete<void>(`/api/admin/marquees/${id}`);
}

/**
 * Admin: Toggle marquee active status
 * Requires moderator+ role
 */
export async function toggleMarquee(id: string): Promise<ApiResponse<MarqueeAdmin>> {
  return api.patch<MarqueeAdmin>(`/api/admin/marquees/${id}/toggle`, {});
}

/**
 * Admin: Reorder marquees
 * Requires moderator+ role
 */
export async function reorderMarquee(orders: MarqueeOrderItem[]): Promise<ApiResponse<void>> {
  return api.put<void>('/api/admin/marquees/reorder', { items: orders });
}

/**
 * Admin: Get marquee settings
 * Requires admin role
 */
export async function getSettings(): Promise<ApiResponse<MarqueeSettings>> {
  return api.get<MarqueeSettings>('/api/admin/marquee-settings');
}

/**
 * Admin: Update marquee settings
 * Requires admin role
 */
export async function updateSettings(
  settings: Partial<MarqueeSettings>
): Promise<ApiResponse<MarqueeSettings>> {
  return api.put<MarqueeSettings>('/api/admin/marquee-settings', settings);
}
